import React, { Fragment, useState } from 'react';
import { Modal } from '@/components';
import '@/assets/styles/components/student-package-info-modal.styles.scss';
import ClassNames from 'classnames';
import { Choose, If, When } from 'vite-plugin-react-control-statements';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { LIVE_SERVICE_TYPES } from '@/constants';
import dayjs from 'dayjs';
import IconPackage from '@/assets/svg/package.svg';
import IconCoach from '@/assets/svg/coach-logo.svg';
import IconGotIt from '@/assets/svg/got-it-logo.svg';
import IconSolver from '@/assets/svg/solver-logo.svg';
import type { StudentCreditInformationResponse, StudentPackageResponse } from '@dopingtech/live_expert_bff_api_ts';
import { useSizes } from '@/utility/responsive.ts';
import type { StudentLiveServiceTypeCreditInformationResponse } from '@dopingtech/live_expert_bff_api_ts/api.ts';

type IStudentPackageInfoModal = {
	isOpen: boolean;
	onClose: () => void;
	studentId: string;
};

const APP_LOGO_MAP = {
	[LIVE_SERVICE_TYPES.COACHING]: IconCoach,
	[LIVE_SERVICE_TYPES.GOT_IT]: IconGotIt,
	[LIVE_SERVICE_TYPES.SOLVER]: IconSolver
};

const APP_NAME_MAP = {
	[LIVE_SERVICE_TYPES.COACHING]: 'Koçum Yanımda',
	[LIVE_SERVICE_TYPES.GOT_IT]: 'Şimdi Anladım',
	[LIVE_SERVICE_TYPES.SOLVER]: 'Çözücü'
};

const MEETING_SERVICE_MAP: Record<string, string> = {
	LECTURE: 'Anlık',
	GUIDANCE: 'Anlık',
	ONE_ON_ONE_COACHING: 'Randevulu',
	ONE_ON_ONE_LECTURE: 'Randevulu',
	COACH_MESSAGE: 'Mesajlaşma'
};

const TAB_LIST = [
	{ label: 'Paketler', id: 'packages' },
	{ label: 'Krediler', id: 'credits' }
];

const StudentPackageInfoModal: React.FC<IStudentPackageInfoModal> = ({ isOpen, onClose, studentId }) => {
	const [activeTab, setActiveTab] = useState<string>(TAB_LIST[0].id);
	const { isMobile } = useSizes();

	const { StudentControllerApi } = useApi();

	const { data: studentPackageData } = useQueryProvider<any, any, StudentPackageResponse>(
		[QUERY_KEYS.findStudentPackageByStudentId, activeTab, studentId],
		() => StudentControllerApi.findStudentPackageByStudentId(Number(studentId)),
		{
			select: ({ data }) => data.data.sort((a: any, b: any) => Number(b.active) - Number(a.active)),
			refetchOnWindowFocus: true,
			enabled: isOpen
		}
	);

	const { data: studentCreditInformationData } = useQueryProvider(
		[QUERY_KEYS.findStudentCreditInformation, activeTab, studentId],
		() => StudentControllerApi.findStudentCreditInformation(Number(studentId)),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: true,
			enabled: isOpen
		}
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			showCloseButton
			size="lg"
			variant={isMobile ? 'bottom' : 'centered'}
			className="student-package-info"
			bodyClassName="student-package-info-body">
			<div className="student-package-info-header">
				<div className="title">Paket Bilgileri</div>
				<div className="description">Aşağıdaki listeden Aktif ve Pasif olan tüm paketlerinize ulaşabilirsiniz.</div>
			</div>

			<div className="student-package-info-content">
				<div className="info-tabs">
					{TAB_LIST.map(item => {
						return (
							<button
								key={item.id}
								className={ClassNames('btn-tab', {
									active: activeTab === item.id
								})}
								onClick={() => setActiveTab(item.id)}>
								{item.label}
							</button>
						);
					})}
				</div>

				<Choose>
					<When condition={activeTab === TAB_LIST[0].id}>
						<div className="student-package-list">
							{studentPackageData?.map((item: StudentPackageResponse | null) => {
								return (
									<div
										key={item?.id}
										className="package-card">
										<div className="package-card__icon">
											<img
												src={IconPackage}
												alt="Package"
											/>
										</div>
										<div className="package-card__content">
											<h3 className="title">{item?.name}</h3>
											<div className="subtitle">
												<div className="date-group">
													<p
														className={ClassNames('date', {
															'is-passive': !item?.active
														})}>
														Paket başlangıç tarihi: {dayjs(item?.activateDate).format('DD.MM.YYYY')}
													</p>
													<p
														className={ClassNames('date', {
															'is-passive': !item?.active
														})}>
														Paket bitim tarihi: {dayjs(item?.expiryDate).format('DD.MM.YYYY')}
													</p>
												</div>
												<If condition={!item?.active}>
													<span className="status">Pasif</span>
												</If>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</When>
					<When condition={activeTab === TAB_LIST[1].id}>
						<div className="student-credit-list">
							{studentCreditInformationData?.map((item: StudentCreditInformationResponse) => {
								return (
									<Fragment>
										{item?.liveServiceTypeInformationList
											?.filter(i => i?.liveServiceType !== 'COACH_MESSAGE')
											.map((infoItem: StudentLiveServiceTypeCreditInformationResponse) => (
												<div className="credit-info-card">
													<div className="credit-info-card__left">
														<div className="credit-info-card__icon">
															<img
																src={APP_LOGO_MAP[item?.liveServiceGroup ?? LIVE_SERVICE_TYPES.COACHING]}
																alt=""
															/>
														</div>
														<div className="credit-info-card__content">
															<div className="badge-wrapper">
																<If condition={infoItem?.liveServiceType !== 'QUESTION_SOLUTION'}>
																	<span className="badge">{MEETING_SERVICE_MAP[infoItem?.liveServiceType || MEETING_SERVICE_MAP.LECTURE]}</span>
																</If>
															</div>
															<h3 className="name">{APP_NAME_MAP[item?.liveServiceGroup || LIVE_SERVICE_TYPES.COACHING]}</h3>
														</div>
													</div>
													<div className="credit-info-card__right">
														<div className="credit-info-card__stat">
															<span className="credit-info-card__stat-label">Toplam kredi</span>
															<span className="credit-info-card__stat-value">{infoItem?.totalCredit || 0}</span>
														</div>
														<div className="divider"></div>
														<div className="credit-info-card__stat">
															<span className="credit-info-card__stat-label">Kalan kredi</span>
															<span className="credit-info-card__stat-value">{infoItem?.remainingCredit || 0}</span>
														</div>
													</div>
												</div>
											))}
									</Fragment>
								);
							})}
						</div>
					</When>
				</Choose>
			</div>
		</Modal>
	);
};

export default StudentPackageInfoModal;
